import { TableActionButton, TableActionIconButton } from "_shared/_views/tables/table-toolbar";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import AddCommentIcon from "@mui/icons-material/AddComment";
import ArchiveIcon from "@mui/icons-material/Archive";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import GroupIcon from "@mui/icons-material/Group";
import SavedSearchIcon from "@mui/icons-material/SavedSearch";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import MessageIcon from "@mui/icons-material/Message";
import PageviewIcon from "@mui/icons-material/Pageview";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import PolicyIcon from "@mui/icons-material/Policy";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, SxProps, Theme } from "@mui/material/styles";
import type { TableRowProps } from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import type { ApiErrorResult } from "_shared/_errors/api-error";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { MarkReadIcon, MarkUnreadIcon, ReadStateToggleIcon } from "_shared/_views/buttons/read-state-icons";
import * as Config from "config";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  createMRTColumnHelper,
  MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_RowData as RowData,
  type MRT_TableInstance as TableInstance,
  type MRT_Column as TableColumn,
  type MRT_Updater,
} from "material-react-table";
import {
  ToolbarButtons,
  searchFieldSpacing,
} from "_shared/_views/tables/table-toolbar";
import type { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import { useSearchIndividualsQuery } from "marketplace/_api/marketplace-api";
import ChatDialog from "messaging/forums/_views/components/forum-chat-dialog";
import ConversationTable from "messaging/forums/_views/components/forums-table";
import {
  type CommunicationTableSearchState,
  type SortingState,
  applyUpdater,
  getColumnFiltersFromSearchParams,
  getGlobalFilterFromSearchParams,
  getNotificationQueryFromSearchParams,
  getPaginationFromSearchParams,
  getSearchParamsFromTableState,
  getSortingFromSearchParams,
  notificationCountsCriteria,
  shouldPopulateNotificationQuery,
  UNREAD_INBOX_CRITERIA,
} from "notifications/_views/communications-page-table-state";
import {
  ACTION_REQUIRED_PARAMETER,
  ARCHIVED_BOX,
  BOX_PARAMETER,
  chosenBoxes,
  COMMUNICATIONS_CURRENT_VIEW,
  COMPOSE_SEGMENT,
  communicationChipGroups,
  communicationPeopleColumns,
  communicationsComposePath,
  communicationsSearchPlaceholder,
  communicationsSubject,
  communicationsTabOfSegment,
  communicationsTabPath,
  CommunicationsTab,
  conversationChipGroups,
  isChipOn,
  listsInbox,
  readCommunicationChips,
  SENT_BOX,
  UNREAD_PARAMETER,
  writeCommunicationChips,
} from "notifications/_views/communications-list-chips";
import { BoxType } from "notifications/notification-box-type";
import { useCurrentTableView } from "_shared/_views/list-pages/use-current-table-view";
import {
  ListPageHeading,
  ListPageMobileSearch,
  ListPageMobileToolbar,
  ListPageTabs,
  ListPageToolbarButtons,
} from "_shared/_views/list-pages/list-page-heading";
import ListChips from "_shared/_views/list-pages/list-chips";
import {
  readListChips,
  writeListChips,
  type ListChipCounts,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";
import { useGetUnreadForumCountQuery } from "messaging/_api/messaging-api";
import {
  useArchiveNotificationMutation,
  useArchiveNotificationsMutation,
  useMarkNotificationAsSeenMutation,
  useMarkNotificationAsUnseenMutation,
  useMarkNotificationsAsSeenMutation,
  useMarkNotificationsAsUnseenMutation,
  useNotificationListCounts,
  useUnarchiveNotificationMutation,
} from "notifications/_api/notifications-api";
import Notification from "notifications/notification";
import {
  handleNotificationMutationFailure,
  type NotificationOperation,
} from "notifications/notification-error";
import {
  isNotificationNavigable,
  NotificationLink,
  NotificationNavigationState,
  NotificationType,
} from "notifications/notification-type";
import {
  addNotification,
  addNotifications,
  getErrorLoadingNotificationsByQuery,
  getIsLoadingNotificationsByQuery,
  getLastSuccessfulNotificationResponseByInterface,
  getNotificationQueryByInterface,
  getNotificationsByQuery,
  isNotificationLoadIssue,
  NotificationLoadIssue,
  populateNotificationsByQuery,
  updateNotificationInterfaceQuery,
} from "notifications/_stores/notifications-redux-slice";
import useNotificationProposals, {
  type NotificationProposal,
  type NotificationProposalDetails,
} from "notifications/_views/_hooks/use-notification-proposals";
import useNotificationWorkAgreements, {
  type NotificationWorkAgreement,
} from "notifications/_views/_hooks/use-notification-work-agreements";
import NotificationContent, {
  getMessageNotificationConversation,
  getRolledRecordCategory,
} from "notifications/_views/notification-content";
import { Action } from "notifications/notification-action";
import TopicCategory from "notifications/topics/topic-category";
import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useSelector } from "react-redux";
import { useLoaderData, useLocation, useNavigate, useSearchParams } from "react-router";
import { ColumnFilterChips } from "_shared/_views/list-pages/column-filter-chips";
import { useSession } from "users/__session/session-context";
import Individual from "marketplace/individuals/individual";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { skipToken } from "@reduxjs/toolkit/query";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import { KeptTrail, KeptTrailLead, KeptTrailLine } from "_shared/_views/kept-trail/kept-trail-line";
import WorkVersion from "work/work-agreements/versions/work-version";
import SearchMatch from "_shared/_views/tables/search-match";
import InputAdornment from "@mui/material/InputAdornment";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { ViewsMenu } from "_shared/_views/list-pages/views-menu";
import { TimestampCell } from "_shared/_views/tables/date-cells";

const ToolbarActionButtons = styled("div")(() => ({
  textAlign: "right",
  whiteSpace: "nowrap",
}));
const categoryDetailFontSize = "0.75rem";
const CategoryItem = styled(ListItem)(({ theme }) => ({
  margin: 0,
  padding: 0,
  minWidth: 0,
  // Every row is as tall as one with a line under its subject, and a row without that line centers
  // its subject in the same height. A page mixing one-, two- and three-line rows read as uneven
  // (#6854).
  // The margin is pinned because MUI gives a two-line ListItemText 6px and a one-line one 4px,
  // which alone kept the two kinds of row 4px apart.
  "& .MuiListItemText-root": {
    minWidth: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  // Only a table row reserves the second line. A phone card's title is lined up with the card's
  // checkbox, and a one-line title centered in two lines' height sat half a line below it (#6719).
  ".MuiTableCell-root & .MuiListItemText-root": {
    margin: theme.spacing(0.5, 0),
    minHeight: `calc(${theme.typography.body2.lineHeight} * (${theme.typography.body2.fontSize} + ${categoryDetailFontSize}))`,
  },
  "& .MuiListItemText-primary, & .MuiListItemText-secondary": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiListItemText-secondary": {
    fontSize: categoryDetailFontSize,
  },
  [theme.breakpoints.down("md")]: {
    "& .MuiListItemIcon-root": {
      display: "none",
    },
  },
}));
// The Description uses the second line every row already has, rather than cutting the row's own
// message off after one (#6854). TruncatedCell still reveals the whole of it on hover, since a
// line-clamped element counts as clipped.
const DescriptionLines = styled("div")(() => ({
  display: "-webkit-box",
  overflow: "hidden",
  whiteSpace: "normal",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));

/**
 * The From column's cell. The search reads the sender's name the notification was written with, so
 * that name marks what matched; the directory's name for a sender without one was never searched
 * (#6590). Exported so it can be covered directly, as the cells below are.
 */
export const SenderCell = ({
  senderUserId,
  senderName,
  sender,
  isLoading,
  table,
}: {
  senderUserId?: Guid;
  senderName?: string;
  sender?: Individual;
  isLoading: boolean;
  table: TableInstance<NotificationType | NotificationLoadIssue>;
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (senderName) {
    return (
      <TruncatedCell>
        <SearchMatch text={senderName} table={table} />
      </TruncatedCell>
    );
  }
  if (!senderUserId) return <TruncatedCell>System</TruncatedCell>;
  if (isLoading && !sender) return <TruncatedCell>Loading...</TruncatedCell>;
  if (!sender) return <NoValueDash />;

  return <TruncatedCell>{sender.fullName}</TruncatedCell>;
};

/**
 * The To column's cell. As with From, only the recipient's name the notification was written with is
 * searched, and only it marks a match (#6590).
 */
export const RecipientsCell = ({
  recipientUserIds,
  recipientName,
  recipients,
  isLoading,
  table,
}: {
  recipientUserIds?: Guid[];
  recipientName?: string;
  recipients: Individual[];
  isLoading?: boolean;
  table: TableInstance<NotificationType | NotificationLoadIssue>;
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (recipientName) {
    return (
      <TruncatedCell>
        <SearchMatch text={recipientName} table={table} />
      </TruncatedCell>
    );
  }
  if (!recipientUserIds || recipientUserIds.length === 0) {
    return <NoValueDash />;
  }

  if (isLoading) return <TruncatedCell>Loading...</TruncatedCell>;
  if (!recipients.length) return <NoValueDash />;

  return (
    <TruncatedCell>
      {recipients.map((recipient, index) => {
        if (!recipient) return null;
        return (
          <React.Fragment key={recipient?.id.value}>
            {recipient.fullName}
            {index < recipients.length - 1 && ", "}
          </React.Fragment>
        );
      })}
    </TruncatedCell>
  );
};

type IndividualFilterOption = {
  label: string;
  secondaryLabel?: string;
  value: string;
};

const INDIVIDUAL_FILTER_SEARCH_DEBOUNCE_MS = 250;

/**
 * An inbox row's read-state action: Mark as Read on an unread row, Mark as Unread on a read one. The
 * icon changes with the label, so a read row does not offer Mark as Unread under a Mark Read icon (#6262).
 * It takes the row and both mutations, rather than callbacks already bound to the row, so which one a
 * click runs, and on which row, is decided here where it can be tested; the page has no render harness.
 */
export function readStateRowAction(
  notification: NotificationType,
  isSeen: boolean,
  markAsRead: (notification: NotificationType) => Promise<void>,
  markAsUnread: (notification: NotificationType) => Promise<void>,
): RowAction {
  return {
    key: "seen",
    label: isSeen ? "Mark as Unread" : "Mark as Read",
    icon: <ReadStateToggleIcon read={isSeen} />,
    onClick: () => void (isSeen ? markAsUnread(notification) : markAsRead(notification)),
  };
}

/** The notification ids of the rows the reader has ticked. */
export function selectedNotificationIds(rowSelection: Record<string, boolean>): string[] {
  return Object.keys(rowSelection).filter((id) => rowSelection[id]);
}

/**
 * Applies one bulk mark-seen or mark-unseen to the ticked rows.
 *
 * Every notification the API returns is folded into a **single** `addNotifications` dispatch. Doing
 * it one at a time cleared the query cache once per row, so ticking a screenful and marking it read
 * re-rendered the table once per notification (GitHub issue #2963).
 *
 * It lives at module scope rather than inside the component so the two callers share one
 * implementation, and so the React Compiler leaves its `try`/`catch` control flow alone.
 */
export async function applyBulkSeenChange({
  notificationIds,
  changeNotifications,
  dispatch,
  clearSelection,
  handleError,
}: Readonly<{
  notificationIds: string[];
  changeNotifications: (notificationIds: string[]) => Promise<Notification[] | undefined>;
  dispatch: (action: ReturnType<typeof addNotifications>) => void;
  clearSelection: () => void;
  handleError: (error: unknown) => unknown | Promise<unknown>;
}>): Promise<void> {
  try {
    const updatedNotifications = await changeNotifications(notificationIds);
    if (updatedNotifications) {
      dispatch(addNotifications(updatedNotifications));
    }
    clearSelection();
  } catch (error) {
    await handleError(error);
  }
}

/**
 * Restores selected notifications one at a time because the API only offers a single-item restore.
 * A deleted notification is already absent from the desired projection, so keep restoring the
 * remaining rows and refresh once at the end. Other failures stop the batch to avoid an ambiguous
 * partial write.
 */
export async function applyBulkUnarchiveChange({
  notificationIds,
  unarchiveNotification,
  refresh,
  clearSelection,
  handleError,
}: Readonly<{
  notificationIds: string[];
  unarchiveNotification: (notificationId: string) => Promise<void>;
  refresh: () => Promise<void>;
  clearSelection: () => void;
  handleError: (error: unknown) => Promise<ApiErrorResult>;
}>): Promise<void> {
  for (const notificationId of notificationIds) {
    try {
      await unarchiveNotification(notificationId);
    } catch (error) {
      const result = await handleError(error);
      if (result.status === 404) continue;
      return;
    }
  }

  await refresh();
  clearSelection();
}

/**
 * Icon for a notification's topic category — and, for proposals, its `actionRequired`.
 *
 * The proposal branch is total by construction: an action this bundle has no specific icon for
 * still gets an icon rather than an empty slot beside a perfectly readable notification.
 */
export function notificationCategoryIcon(topicCategory: TopicCategory, actionRequired: string | undefined) {
  if (topicCategory === TopicCategory.Messaging.Forum || topicCategory === TopicCategory.Messaging.ForumInvitation) {
    return <MessageIcon />;
  }
  if (
    topicCategory === TopicCategory.Users.NetworkConnection ||
    topicCategory === TopicCategory.Users.NetworkInvitation
  ) {
    return <AccountTreeIcon />;
  }
  if (topicCategory === TopicCategory.Work.Proposal) {
    if (!actionRequired) return <PersonAddIcon />;
    if (actionRequired === Action.Review || actionRequired === Action.ProposalReReviewRequested) {
      return <PageviewIcon />;
    }
    if (
      actionRequired === Action.ProposalReviewerAccessChanged ||
      actionRequired === Action.DraftProposalReviewerAccessChanged
    ) {
      return <ManageAccountsIcon />;
    }
    if (actionRequired === Action.ProposalReviewerRemoved) {
      return <PersonRemoveIcon />;
    }
    return <AssignmentIcon />;
  }
  // How a policy replacement's first pass went, or an agreement in it that stopped (#6256).
  if (topicCategory === TopicCategory.Work.PolicyReplacement) return <PolicyIcon />;
  if (topicCategory === TopicCategory.Work.Agreement) return <WorkIcon />;
  if (topicCategory === TopicCategory.Marketplace.Team) return <GroupIcon />;
  if (topicCategory === TopicCategory.Marketplace.SavedSearch) return <SavedSearchIcon />;
  if (topicCategory === TopicCategory.Marketplace.IndividualVisibility) return <VisibilityOffIcon />;
  if (topicCategory === TopicCategory.Marketplace.TeamInvitation) return <GroupAddIcon />;
  if (topicCategory === TopicCategory.LegalEntities.TeamRoster) return <GroupsIcon />;
  if (topicCategory === TopicCategory.LegalEntities.EntityMember) return <PersonIcon />;
  if (topicCategory === TopicCategory.LegalEntities.EntityMemberInvitation) return <HowToRegIcon />;
  if (topicCategory === TopicCategory.LegalEntities.LegalEntity) return <SupervisorAccountIcon />;
  if (topicCategory === TopicCategory.Permissions.AccessRequest) return <LockOpenIcon />;
  return null;
}

/**
 * The Subject column's cell: what the row is about, and under it what kind of row it is.
 *
 * A row about a proposal, a work agreement or a conversation leads with that thing's name, because
 * it is what the reader scans the inbox for; "Work Proposal" on every proposal row told two rows
 * apart only once they were opened (#5248). The kind of row moves to the line underneath, and a
 * proposal's stage follows it there, so no row draws more than two lines (#6854). A row about
 * nothing with a name of its own — a network connection, a team change — leads with its kind.
 *
 * The subject is bold until the reader has seen the row, the way the bell's rows are (#5147); the
 * row's background says so too, so no text does.
 *
 * Only the kind of row marks the search term: the search reads a row's category, not the names
 * (#6590).
 *
 * Exported so it can be covered directly rather than through a rendered page — see the note at the
 * top of `communications-page.spec.tsx`.
 */
export function notificationSubjectCell(
  notification: NotificationType | NotificationLoadIssue,
  options: Readonly<{
    userId: Guid | undefined;
    proposals?: NotificationProposalDetails;
    /** The work agreements the rows are about, keyed by work agreement id. */
    workAgreements?: ReadonlyMap<string, NotificationWorkAgreement>;
    /** The table whose search the kind of row marks; the search reads a row's category (#6590). */
    table?: TableInstance<NotificationType | NotificationLoadIssue>;
    /** Whether the second line leads with Received or Sent: under Archived, which holds both (#6719). */
    showsDirection?: boolean;
  }>,
): JSX.Element {
  if (isNotificationLoadIssue(notification)) {
    return (
      <CategoryItem dense>
        <ListItemIcon>
          <Icon color="error">error</Icon>
        </ListItemIcon>
        <ListItemText primary={<strong style={{ fontWeight: "normal" }}>Unknown</strong>} />
      </CategoryItem>
    );
  }

  // A rolled row is its record's notification, whatever its newest update was (#6880).
  const topicCategory: TopicCategory = getRolledRecordCategory(notification) ?? notification?.topic?.category ?? "";
  const typeLabel = getNotificationTypeLabel(notification);
  const kind = <span>{options.table ? <SearchMatch text={typeLabel} table={options.table} /> : typeLabel}</span>;
  const { subject, version, stage } = getNotificationSubject(notification, options);
  const direction = options.showsDirection ? notificationDirection(notification, options.userId) : undefined;
  // A proposal whose name has not arrived, or never will, still says its stage under its kind. Under
  // Archived the line leads with which way the row went (#6719).
  const parts = [
    direction && <span key="direction">{direction}</span>,
    subject && <React.Fragment key="kind">{kind}</React.Fragment>,
    stage && <span key="stage">{stage}</span>,
  ].filter(Boolean);
  const detail = parts.length > 0 ? parts.flatMap((part, index) => (index === 0 ? [part] : [" · ", part])) : undefined;

  return (
    <CategoryItem dense>
      <ListItemIcon>{notificationCategoryIcon(topicCategory, notification.actionRequired)}</ListItemIcon>
      <ListItemText
        primary={
          <TruncatedCell component="span">
            <strong style={{ fontWeight: Notification.isSeenByUser(notification, options.userId) ? "normal" : "bold" }}>
              {subject ? (
                // A long name is cut short before the version is: the version tells a change
                // order's rows from the earlier version's, which share the name (#6809).
                <KeptTrailLine variant="inherit" sx={{ fontWeight: "inherit" }}>
                  <KeptTrailLead>{subject}</KeptTrailLead>
                  {version && <KeptTrail>{`${WorkVersion.SEPARATOR}${version}`}</KeptTrail>}
                </KeptTrailLine>
              ) : kind}
            </strong>
          </TruncatedCell>
        }
        secondary={detail ? <TruncatedCell component="span">{detail}</TruncatedCell> : undefined}
      />
    </CategoryItem>
  );
}

/**
 * Which way a notification went, as the reader sees it: Received when it was to them, Sent otherwise.
 * The archive holds both, so its rows say which (#6719).
 */
export function notificationDirection(notification: NotificationType, userId: Guid | undefined): "Received" | "Sent" {
  return Notification.userIsRecipient(notification, userId) ? "Received" : "Sent";
}

/**
 * The name a row is about — a message's conversation, a proposal's or a work agreement's name — the
 * version of a proposal's or a work agreement's work, and the stage of a proposal.
 *
 * Most proposal notifications also name the proposal in their own message, but at a different place
 * in each wording, so a row cannot rely on it. The stage is where the proposal stands now, not when
 * the row was sent; by the time it is read the proposal has usually moved on (#5129). A message
 * row's conversation — "Acme Litigation · Client Team" — is the API's own subheader (#5825).
 */
function getNotificationSubject(
  notification: NotificationType,
  options: Readonly<{
    proposals?: NotificationProposalDetails;
    workAgreements?: ReadonlyMap<string, NotificationWorkAgreement>;
  }>,
): Readonly<{ subject?: string; version?: string; stage?: string }> {
  const proposal = getNotificationProposal(notification, options.proposals);
  const stage = proposal?.statusLabel ?? getProposalStatusPlaceholder(notification, options.proposals);

  const conversation = getMessageNotificationConversation(notification);
  if (conversation) return { subject: conversation, stage };
  if (proposal?.name) return { subject: proposal.name, version: proposal.version, stage };

  const workAgreement = getNotificationWorkAgreement(notification, options.workAgreements);
  return { subject: workAgreement?.name, version: workAgreement?.version, stage };
}

/**
 * The Description column's cell: the notification's own content, on up to two lines. A row that folded
 * several messages together (#830) reads as the newest of them followed by "and 2 more" (#5825); its
 * conversation is the row's subject instead. Whether an archived row was received or sent is on the
 * subject's second line (#6719).
 *
 * Exported so it can be covered directly rather than through a rendered page — see the note at the
 * top of `communications-page.spec.tsx`.
 */
export function notificationDescriptionCell(notification: NotificationType | NotificationLoadIssue): JSX.Element {
  if (isNotificationLoadIssue(notification)) {
    return <Typography color="error">There was an issue loading this notification&apos;s details.</Typography>;
  }
  // A structured notification stacks its subheader, body, and fine print as separate lines, each
  // clipped on its own, and the cell's own text runs them together with nothing between them. So the
  // hover is handed the parts with their breaks restored. A notification without them renders one
  // line of markdown, and the text of that line as the reader sees it is already the whole of it. So
  // does a message row, whose subheader is its conversation and is the row's subject instead:
  // its newest message and "and 2 more" are one line, and its own text is the whole of it (#5825).
  const structuredContent =
    notification.topic?.category === TopicCategory.Messaging.Forum
      ? ""
      : [notification.subheader, notification.body, notification.finePrint].filter(Boolean).join("\n");
  return (
    <TruncatedCell title={structuredContent}>
      <DescriptionLines>
        <NotificationContent notification={notification} hideConversation />
      </DescriptionLines>
    </TruncatedCell>
  );
}

/**
 * The proposal this row is about, once it has arrived.
 *
 * Nothing at all for a row about something else, and nothing once the request has finished without
 * the proposal in it — which is the answer for a proposal the reader may not open.
 */
function getNotificationProposal(
  notification: NotificationType,
  proposals: NotificationProposalDetails | undefined,
): NotificationProposal | undefined {
  const topicId = getNotificationProposalTopicId(notification);
  if (!topicId) return undefined;

  return proposals?.byId.get(topicId);
}

/**
 * `Loading...` where the stage goes while the proposals are still on their way.
 *
 * They come back in their own request, seconds after the rows on a cold start, so a proposal row
 * would otherwise sit with a blank where its stage goes and read as a proposal that has none.
 * `Loading...` is what the From and To columns say in the same situation. The name says nothing in
 * the meantime: two placeholders stacked would say the same thing twice.
 */
function getProposalStatusPlaceholder(
  notification: NotificationType,
  proposals: NotificationProposalDetails | undefined,
): string | undefined {
  if (!proposals?.isLoading) return undefined;

  return getNotificationProposalTopicId(notification) ? "Loading..." : undefined;
}

/**
 * The id of the proposal a row is about, or nothing for a row about anything else.
 *
 * It is the topic's *category* that says a row is about a proposal, not its id: a row about
 * something else must never borrow a proposal from an id that happens to match.
 */
function getNotificationProposalTopicId(notification: NotificationType): string | undefined {
  if (notification.topic?.category !== TopicCategory.Work.Proposal) return undefined;

  return notification.topic?.id?.value;
}

/**
 * The work agreement a row is about, once it has arrived. Nothing for a row about anything else,
 * for the same reason a proposal is found by category as well as by id.
 */
function getNotificationWorkAgreement(
  notification: NotificationType,
  workAgreements: ReadonlyMap<string, NotificationWorkAgreement> | undefined,
): NotificationWorkAgreement | undefined {
  if (notification.topic?.category !== TopicCategory.Work.Agreement) return undefined;

  const topicId = notification.topic?.id?.value;
  return topicId ? workAgreements?.get(topicId) : undefined;
}

function notificationRowStyle(
  state: Readonly<{ backgroundColor: string; isArchived: boolean; onSelect?: () => void }>,
): TableRowProps {
  const sx: SxProps<Theme> = {
    backgroundColor: state.backgroundColor,
    cursor: state.onSelect && !state.isArchived ? "pointer" : "default",
  };

  return {
    hover: Boolean(state.onSelect),
    onClick: state.onSelect,
    sx,
  };
}

/**
 * How one row of the communications table looks, and whether clicking it goes anywhere.
 *
 * A notification whose category has no route gets no click handler, no hover highlight, and no
 * pointer cursor, so the row never offers navigation it cannot perform — network connection
 * announcements are the case this was added for (#4849). The row's own controls (mark read,
 * archive) are unaffected; they are buttons inside the row with their own handlers.
 */
export function notificationRowProps(
  notification: NotificationType | NotificationLoadIssue,
  options: Readonly<{ isArchived: boolean; userId: Guid | undefined; onSelect: () => void }>,
): TableRowProps {
  if (isNotificationLoadIssue(notification)) {
    // A row that failed to load keeps the click it has always had, even though it goes nowhere.
    return notificationRowStyle({
      backgroundColor: options.isArchived ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 100, 100, 0.1)",
      isArchived: options.isArchived,
      onSelect: options.onSelect,
    });
  }

  const isSeen = Notification.isSeenByUser(notification, options.userId);
  return notificationRowStyle({
    backgroundColor: options.isArchived || isSeen ? "rgba(0, 0, 0, 0.04)" : "inherit",
    isArchived: options.isArchived,
    onSelect: isNotificationNavigable(notification) ? options.onSelect : undefined,
  });
}

/**
 * The actions cell of one row of the Notifications tab, whose actions depend on the box the row is in.
 *
 * The inbox marks read and archives; a sent row archives, which puts away the current user's copy and
 * leaves every recipient's alone (#6263); an archived row restores. Sent and Archived list together, so
 * it is the row, not the chips, that says which (#6719). A row that failed to load has nothing to act on.
 */
export function communicationRowActionsCell(
  communication: NotificationType | NotificationLoadIssue,
  options: Readonly<{
    /** Whether the list is the inbox, where a row is read or unread. */
    isInbox: boolean;
    /** Whether the reader archived this row. */
    isArchived: boolean;
    userId: Guid | undefined;
    onMarkRead: (communication: NotificationType) => Promise<void>;
    onMarkUnread: (communication: NotificationType) => Promise<void>;
    onArchive: (communication: NotificationType) => Promise<void>;
    onUnarchive: (communication: NotificationType) => Promise<void>;
  }>,
) {
  if (isNotificationLoadIssue(communication)) return null;
  return (
    <RowActions
      ariaLabel={`Actions for ${getNotificationTypeLabel(communication)}`}
      actions={communicationRowActions(communication, options)}
    />
  );
}

function communicationRowActions(
  communication: NotificationType,
  options: Parameters<typeof communicationRowActionsCell>[1],
): RowAction[] {
  if (options.isArchived) {
    return [
      {
        key: "unarchive",
        label: "Restore from Archive",
        icon: <UnarchiveIcon />,
        onClick: () => void options.onUnarchive(communication),
      },
    ];
  }
  const archive: RowAction = {
    key: "archive",
    label: "Archive",
    icon: <ArchiveIcon />,
    onClick: () => void options.onArchive(communication),
  };
  if (!options.isInbox) return [archive];
  const isSeen = Notification.isSeenByUser(communication, options.userId);
  return [readStateRowAction(communication, isSeen, options.onMarkRead, options.onMarkUnread), archive];
}

/**
 * Whether the reader archived a row. The API says so of every row it sends, realtime payloads
 * included; a row cached before #6719 does not, and is archived exactly when the list is the archive
 * alone.
 */
export function isCommunicationArchived(
  communication: NotificationType | NotificationLoadIssue,
  selection: ListChipSelection,
): boolean {
  const boxes = chosenBoxes(selection);
  const listsArchiveOnly = boxes.length === 1 && boxes[0] === ARCHIVED_BOX;
  if (isNotificationLoadIssue(communication)) return listsArchiveOnly;
  return communication.isArchived ?? listsArchiveOnly;
}

/** A bulk action of the Notifications tab. */
export type CommunicationBulkAction = "markRead" | "markUnread" | "archive" | "unarchive";

/**
 * The bulk actions each box offers (#6719): Mark read, Mark unread and Archive in the inbox, Archive
 * under Sent, Un-archive under Archived, and both of the last under Sent and Archived together, where
 * each reaches the ticked rows it fits.
 */
export function communicationBulkActions(selection: ListChipSelection): readonly CommunicationBulkAction[] {
  const boxes = chosenBoxes(selection);
  if (boxes.length === 0) return ["markRead", "markUnread", "archive"];
  const actions: CommunicationBulkAction[] = [];
  if (boxes.includes(SENT_BOX)) actions.push("archive");
  if (boxes.includes(ARCHIVED_BOX)) actions.push("unarchive");
  return actions;
}

const BULK_ACTION_LABELS: Readonly<Record<CommunicationBulkAction, { label: string; title: string; icon: JSX.Element }>> = {
  markRead: { label: "Mark Read", title: "Mark as Read", icon: <MarkReadIcon /> },
  markUnread: { label: "Mark Unread", title: "Mark as Unread", icon: <MarkUnreadIcon /> },
  archive: { label: "Archive", title: "Archive", icon: <ArchiveIcon /> },
  unarchive: { label: "Un-Archive", title: "Un-Archive", icon: <UnarchiveIcon /> },
};

/**
 * The ticked rows' notification ids, split by whether the reader archived them: Archive reaches the
 * others, and Un-archive the archived ones. A ticked id that is no longer on the page is in neither.
 */
export function selectedCommunications(
  rowSelection: Record<string, boolean>,
  rows: readonly (NotificationType | NotificationLoadIssue)[],
  selection: ListChipSelection,
): Readonly<{ active: string[]; archived: string[] }> {
  const ticked = new Set(selectedNotificationIds(rowSelection));
  const active: string[] = [];
  const archived: string[] = [];
  for (const row of rows) {
    const id = row.id?.value;
    if (!id || !ticked.has(id)) continue;
    if (isCommunicationArchived(row, selection)) archived.push(id);
    else active.push(id);
  }
  return { active, archived };
}

function CommunicationIndividualFilter({
  column,
  individuals,
  label,
}: Readonly<{
  column: TableColumn<NotificationType | NotificationLoadIssue, string>;
  individuals: Individual[];
  label: string;
}>) {
  const filterValue = column.getFilterValue() as string | undefined;
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");

  useEffect(() => {
    const trimmedInput = inputValue.trim();
    const timeout = globalThis.setTimeout(() => {
      setDebouncedInputValue(trimmedInput);
    }, INDIVIDUAL_FILTER_SEARCH_DEBOUNCE_MS);

    return () => globalThis.clearTimeout(timeout);
  }, [inputValue]);

  const individualSearchQuery = useSearchIndividualsQuery(
    debouncedInputValue
      ? {
          search: {
            from: 0,
            query: debouncedInputValue,
            size: 10,
          },
        }
      : skipToken,
  );

  const searchOptions = useMemo(
    () => getIndividualFilterOptionsFromSearchResults(individualSearchQuery.data?.hits?.map((hit) => hit.data) ?? []),
    [individualSearchQuery.data],
  );

  const selectedOption = useMemo(() => {
    if (!filterValue) return null;

    const selectedIndividual = individuals.find((individual) => individual.userId.value === filterValue);
    if (selectedIndividual) return getIndividualFilterOptionFromIndividual(selectedIndividual);

    return searchOptions.find((option) => option.value === filterValue) ?? { label: filterValue, value: filterValue };
  }, [filterValue, individuals, searchOptions]);

  const options = useMemo(
    () => dedupeIndividualFilterOptions([...(selectedOption ? [selectedOption] : []), ...searchOptions]),
    [searchOptions, selectedOption],
  );
  const displayedInputValue = inputValue || selectedOption?.label || "";

  return (
    <Autocomplete<IndividualFilterOption, false, false, false>
      filterOptions={(options) => options}
      getOptionLabel={(option) => option.label}
      inputValue={displayedInputValue}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      loading={individualSearchQuery.isFetching}
      loadingText="Searching..."
      noOptionsText={inputValue.trim() ? "No individuals found" : "Type a name"}
      onChange={(_event, option) => {
        column.setFilterValue(option?.value || undefined);
        setInputValue("");
      }}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === "reset") return;

        if (
          reason === "clear" ||
          !newInputValue ||
          (filterValue && newInputValue !== selectedOption?.label)
        ) {
          column.setFilterValue(undefined);
        }

        setInputValue(newInputValue);
      }}
      options={options}
      renderOption={(props, option) => (
        <li {...props}>
          <ListItemText primary={option.label} secondary={option.secondaryLabel} />
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Search by name" size="small" variant="standard" />
      )}
      value={selectedOption}
    />
  );
}

const createCommunicationIndividualFilter = (individuals: Individual[], label: string) =>
  function CommunicationIndividualFilterRenderer({
    column,
  }: Readonly<{ column: TableColumn<NotificationType | NotificationLoadIssue, string> }>) {
    return <CommunicationIndividualFilter column={column} individuals={individuals} label={label} />;
  };

const columnHelper = createMRTColumnHelper<NotificationType | NotificationLoadIssue>();

export default function CommunicationsPage() {
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  const routeParams = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // Notifications or Conversations (#6719). The retired tabs' compose and conversation routes keep
  // resolving, each on the tab it became part of.
  const activeTab = communicationsTabOfSegment(routeParams.pageTab);
  const isComposeRoute =
    routeParams.action === "compose" ||
    routeParams.action === "create" ||
    (routeParams.pageTab === COMPOSE_SEGMENT && routeParams.action === undefined);
  // The view the reader left the page in, kept on their account (#6714): a bare `/communications`
  // opens it. The URL holds the rest, so nothing is kept in the session.
  const tableViews = useCurrentTableView(COMMUNICATIONS_CURRENT_VIEW);
  const { remember } = tableViews;

  // #region Chips
  // The boxes are chips on the Notifications tab: none is the inbox, and Sent and Archived list
  // together (#6719). Memoized on the URL because the list's request is built from it in an effect.
  const chipsKey = searchParams.toString();
  const chips = useMemo(() => readCommunicationChips(new URLSearchParams(chipsKey)), [chipsKey]);
  const conversationChips = useMemo(
    () => readListChips(conversationChipGroups, new URLSearchParams(chipsKey)),
    [chipsKey],
  );
  const isInbox = listsInbox(chips);
  const peopleColumns = communicationPeopleColumns(chips);
  const subject = communicationsSubject(chips);
  const bulkActions = communicationBulkActions(chips);
  // #endregion

  const globalFilter = useMemo(() => getGlobalFilterFromSearchParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => getColumnFiltersFromSearchParams(searchParams), [searchParams]);
  const sorting = useMemo(() => getSortingFromSearchParams(searchParams), [searchParams]);
  const pagination = useMemo(() => getPaginationFromSearchParams(searchParams), [searchParams]);
  const navigate = useNavigate();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();
  const dispatch = useAttorneyHubDispatch();
  const { openDialog, closeDialog } = useDialog();
  const isMedDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const interfaceName = "communicationsPage";
  const isNotificationsTab = activeTab === CommunicationsTab.Notifications;

  const notificationQuery = useSelector(getNotificationQueryByInterface(interfaceName));
  const defaultQuery = useMemo(() => getNotificationQueryFromSearchParams(new URLSearchParams(chipsKey)), [chipsKey]);

  const effectiveQuery = notificationQuery ?? defaultQuery;
  const queryResponse = useSelector(getNotificationsByQuery(effectiveQuery));
  const queryIsLoading = useSelector(getIsLoadingNotificationsByQuery(effectiveQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingNotificationsByQuery(effectiveQuery));
  const lastSuccessfulResponse = useSelector(getLastSuccessfulNotificationResponseByInterface(interfaceName));
  const tableNotifications = queryResponse ?? lastSuccessfulResponse;
  const selectedFilterUserIds = useMemo(() => getSelectedNotificationFilterUserIds(columnFilters), [columnFilters]);

  // #region Counts
  // Each tab's badge counts what is unread in it, and does not move with the chips (#6719).
  const unreadInbox = useNotificationListCounts({ criteria: UNREAD_INBOX_CRITERIA });
  const unreadConversations = useGetUnreadForumCountQuery();
  // The chips' counts, under the list's own criteria, from the list's one counts request (#6709).
  const countsCriteria = useMemo(() => notificationCountsCriteria(new URLSearchParams(chipsKey)), [chipsKey]);
  const listCounts = useNotificationListCounts({ criteria: countsCriteria }, { skip: !isNotificationsTab });
  const counts = listCounts.counts;
  const chipCounts = useMemo<ListChipCounts>(
    () => ({
      [BOX_PARAMETER]: { [SENT_BOX]: counts?.boxes[BoxType.Outbox], [ARCHIVED_BOX]: counts?.boxes[BoxType.Archive] },
      [UNREAD_PARAMETER]: { true: counts?.unread },
      [ACTION_REQUIRED_PARAMETER]: { true: counts?.actionRequired },
    }),
    [counts],
  );
  const conversationChipCounts = useMemo<ListChipCounts>(
    () => ({ [UNREAD_PARAMETER]: { true: unreadConversations.data } }),
    [unreadConversations.data],
  );
  // #endregion

  const userIdsToLoad = useMemo(() => {
    const userIds = getValidNotificationUserIds(tableNotifications);
    selectedFilterUserIds.forEach((userId) => userIds.add(userId));
    return userIds;
  }, [selectedFilterUserIds, tableNotifications]);

  const individualQuery = useGetIndividualsByUserIdsQuery(userIdsToLoad.size ? { userIds: [...userIdsToLoad] } : skipToken);
  const proposals = useNotificationProposals(tableNotifications);
  const workAgreements = useNotificationWorkAgreements(tableNotifications);
  const [markNotificationAsSeen] = useMarkNotificationAsSeenMutation();
  const [markNotificationsAsSeen] = useMarkNotificationsAsSeenMutation();
  const [markNotificationAsUnseen] = useMarkNotificationAsUnseenMutation();
  const [markNotificationsAsUnseen] = useMarkNotificationsAsUnseenMutation();
  const [archiveNotification] = useArchiveNotificationMutation();
  const [archiveNotifications] = useArchiveNotificationsMutation();
  const [unarchiveNotification] = useUnarchiveNotificationMutation();

  const refreshNotifications = useCallback(async () => {
    await dispatch(populateNotificationsByQuery({
      session,
      query: effectiveQuery,
      interfaceName,
    }));
  }, [dispatch, effectiveQuery, interfaceName, session]);

  const handleMutationError = useCallback(
    (error: unknown, operation: NotificationOperation, refreshNotFound = true) => handleNotificationMutationFailure({
      error,
      operation,
      refresh: refreshNotifications,
      notify: (message) => enqueueSnackbar(message, { variant: "error" }),
      refreshNotFound,
    }),
    [refreshNotifications],
  );

  const individuals = useMemo(
    () => Individual.fromJSONArray(individualQuery.data, { allowEmpty: true }) ?? [],
    [individualQuery.data],
  );

  // A From or To filter holds a user id: its chip names the person, or says only the column.
  const nameOfIndividual = (_columnId: string, value: string) =>
    individuals.find((individual) => individual.userId.value === value)?.fullName;

  // New Message opens over the tab, and over the list as the reader has it, so closing it goes back
  // to the same rows.
  const openComposeRoute = useCallback(() => {
    navigate(communicationsComposePath(activeTab, location.search));
  }, [activeTab, location.search, navigate]);

  const openMessageDialog = useCallback(
    (id?: Guid) => {
      const closePath = `${communicationsTabPath(activeTab)}${location.search}`;

      openDialog({
        title: id ? "Message" : "New Message",
        resetStack: true,
        MuiProps: {
          fullWidth: true,
          maxWidth: id ? "md" : "sm",
          sx: id ? undefined : { "& .MuiDialog-paper": { maxWidth: 560 } },
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        hideFullScreenButton: true,
        component: <ChatDialog forumId={id} onCancelCompose={() => { closeDialog(); navigate(closePath); }}
          onComposeComplete={() => { closeDialog(); navigate(closePath); }} />,
        onClose: () => void navigate(closePath),
      });
    },
    [activeTab, closeDialog, location.search, navigate, openDialog],
  );

  useEffect(() => {
    if (!notificationQuery) return;
    if (!shouldPopulateNotificationQuery({ notificationQuery, queryIsLoading, errorLoadingQuery, queryResponse })) {
      return;
    }

    dispatch(
      populateNotificationsByQuery({
        session,
        query: notificationQuery,
        interfaceName,
      }),
    );
  }, [dispatch, session, interfaceName, notificationQuery, queryIsLoading, queryResponse, errorLoadingQuery]);

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    if (isComposeRoute) {
      openMessageDialog();
      return;
    }
    if (activeTab !== CommunicationsTab.Conversations) return;
    // `/communications/{forums|conversations}/view/:id`, and `/communications/{forums|conversations}/:id`.
    const forumId = routeParams.id ?? routeParams.action;
    if (forumId && Guid.isValid(forumId)) openMessageDialog(new Guid(forumId));
  }, [
    activeTab,
    isComposeRoute,
    routeParams.action,
    routeParams.id,
    session.user?.legalEntityId,
    openMessageDialog,
  ]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;
    if (!isNotificationsTab) return;

    if (defaultQuery.isEqualTo(notificationQuery)) return;

    dispatch(
      updateNotificationInterfaceQuery({
        interfaceName,
        query: defaultQuery,
      }),
    );
  }, [session.user?.legalEntityId, isNotificationsTab, defaultQuery, notificationQuery, dispatch, interfaceName]);

  /** Shows `next` and keeps it as the reader's view: every change the reader makes goes through here. */
  const showReaderChange = useCallback(
    (next: URLSearchParams) => {
      const query = next.toString();
      const path = communicationsTabPath(activeTab);
      remember(query ? `${path}?${query}` : path);
      setSearchParams(next);
    },
    [activeTab, remember, setSearchParams],
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, tab: CommunicationsTab) => {
      // A tab is a different kind of thing, so nothing the other tab's chips or filters said goes with it.
      const path = communicationsTabPath(tab);
      setRowSelection({});
      remember(path);
      navigate(path);
    },
    [navigate, remember],
  );

  const handleChipsChange = useCallback(
    (selection: ListChipSelection) => {
      // The rows ticked in one box are not in the next, so a bulk action never reaches what is not shown.
      setRowSelection({});
      showReaderChange(writeCommunicationChips(selection, searchParams));
    },
    [searchParams, showReaderChange],
  );

  const handleConversationChipsChange = useCallback(
    (selection: ListChipSelection) => showReaderChange(writeListChips(conversationChipGroups, selection, searchParams)),
    [searchParams, showReaderChange],
  );

  const updateSearchParamsFromTableState = useCallback(
    (nextState: CommunicationTableSearchState) => {
      const newSearchParams = getSearchParamsFromTableState(nextState, searchParams);
      if (newSearchParams.toString() === searchParams.toString()) return;
      showReaderChange(newSearchParams);
    },
    [searchParams, showReaderChange],
  );

  const setGlobalFilter = useCallback(
    (updater: MRT_Updater<string>) => {
      updateSearchParamsFromTableState({
        globalFilter: applyUpdater(updater, globalFilter),
        columnFilters,
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setColumnFilters = useCallback(
    (updater: MRT_Updater<ColumnFiltersState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters: applyUpdater(updater, columnFilters),
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setSorting = useCallback(
    (updater: MRT_Updater<SortingState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting: applyUpdater(updater, sorting),
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setPagination = useCallback(
    (updater: MRT_Updater<PaginationState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting,
        pagination: applyUpdater(updater, pagination),
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  async function navigateToTarget(notification: NotificationType | NotificationLoadIssue) {
    if (isNotificationLoadIssue(notification)) {
      return;
    }
    try {
      if (isInbox) {
        await markNotificationAsSeen({ notificationId: notification.id.value }).unwrap();
      }
      const redirectUrl = NotificationLink(notification);
      // Categories with no route never reach here from a row click, since those rows carry no click
      // handler at all (#4849). Guard anyway rather than navigate to an empty path.
      if (!redirectUrl) return;
      const navigationState = NotificationNavigationState(notification, `${location.pathname}${location.search}`);
      if (navigationState) {
        navigate(redirectUrl, { state: navigationState });
      } else {
        navigate(redirectUrl);
      }
    } catch (error) {
      await handleMutationError(error, "mark-seen");
    }
  }

  // The ticked rows, split by whether the reader archived them: under Sent and Archived together, Archive
  // reaches the sent ones and Un-archive the archived ones.
  const selectedRows = useMemo(
    () => selectedCommunications(rowSelection, tableNotifications?.data ?? [], chips),
    [chips, rowSelection, tableNotifications],
  );

  const markMultipleSeen = useCallback(
    () =>
      applyBulkSeenChange({
        notificationIds: selectedNotificationIds(rowSelection),
        changeNotifications: (notificationIds) => markNotificationsAsSeen({ notificationIds }).unwrap(),
        dispatch,
        clearSelection: () => setRowSelection({}),
        handleError: (error) => handleMutationError(error, "mark-seen"),
      }),
    [dispatch, handleMutationError, markNotificationsAsSeen, rowSelection],
  );

  const markMultipleUnseen = useCallback(
    () =>
      applyBulkSeenChange({
        notificationIds: selectedNotificationIds(rowSelection),
        changeNotifications: (notificationIds) => markNotificationsAsUnseen({ notificationIds }).unwrap(),
        dispatch,
        clearSelection: () => setRowSelection({}),
        handleError: (error) => handleMutationError(error, "mark-unseen"),
      }),
    [dispatch, handleMutationError, markNotificationsAsUnseen, rowSelection],
  );

  const archiveMultiple = useCallback(async () => {
    const notificationIds = selectedRows.active;
    if (notificationIds.length === 0) return;

    try {
      await archiveNotifications({ notificationIds }).unwrap();
      await refreshNotifications();
      setRowSelection({});
    } catch (error) {
      await handleMutationError(error, "archive");
    }
  }, [archiveNotifications, handleMutationError, refreshNotifications, selectedRows]);

  const unArchiveMultiple = useCallback(async () => {
    try {
      await applyBulkUnarchiveChange({
        notificationIds: selectedRows.archived,
        unarchiveNotification: (notificationId) => unarchiveNotification({ notificationId }).unwrap(),
        refresh: refreshNotifications,
        clearSelection: () => setRowSelection({}),
        handleError: (error) => handleMutationError(error, "unarchive", false),
      });
    } catch (error) {
      await handleMutationError(error, "unarchive");
    }
  }, [handleMutationError, refreshNotifications, selectedRows, unarchiveNotification]);

  const renderCreatedDateColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue): React.ReactNode => {
      if (isNotificationLoadIssue(notification)) {
        return null;
      }
      return <TimestampCell value={notification.createdDate} />;
    },
    [],
  );

  // Under Archived a row says which way it went, since the archive holds both (#6719).
  const showsDirection = chosenBoxes(chips).includes(ARCHIVED_BOX);
  const renderSubjectColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue, table: TableInstance<NotificationType | NotificationLoadIssue>) =>
      notificationSubjectCell(notification, { userId: session.user?.id, proposals, workAgreements, table, showsDirection }),
    [proposals, session.user?.id, showsDirection, workAgreements],
  );

  const renderDescriptionColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue) => notificationDescriptionCell(notification),
    [],
  );

  const renderFromColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue, table: TableInstance<NotificationType | NotificationLoadIssue>) => {
      if (isNotificationLoadIssue(notification)) return null;

      const senderName =
        "messageInfo" in notification ? notification?.messageInfo?.senderName?.value : notification?.senderName?.value;
      const sender = individuals?.find((individual) => {
        return individual.userId.isEqualTo(notification.senderUserId);
      });
      const isLoading = individualQuery.isLoading;

      return (
        <SenderCell
          senderUserId={notification.senderUserId}
          senderName={senderName}
          sender={sender}
          isLoading={isLoading}
          table={table}
        />
      );
    },
    [individuals, individualQuery.isLoading],
  );

  const renderToColumn = useCallback(
    (notification: NotificationType | NotificationLoadIssue, table: TableInstance<NotificationType | NotificationLoadIssue>) => {
      if (isNotificationLoadIssue(notification)) return null;

      const recipients = notification.recipients?.map((r) => r.userId);
      if (!recipients?.length) return <NoValueDash />;
      const recipientIndividuals = individuals.filter((individual) =>
        recipients.some((recipientUserId) => individual.userId.isEqualTo(recipientUserId)),
      );

      return (
        <RecipientsCell
          recipientUserIds={recipients}
          recipientName={notification.recipientName?.value}
          recipients={recipientIndividuals}
          isLoading={individualQuery.isLoading}
          table={table}
        />
      );
    },
    [individuals, individualQuery.isLoading],
  );
  async function markAsRead(communication: NotificationType) {
    if (!communication || communication?.seenByUserAt(session.user?.id)) return;
    try {
      const updatedNotification = await markNotificationAsSeen({ notificationId: communication.id.value }).unwrap();
      if (!updatedNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(updatedNotification));
    } catch (error) {
      await handleMutationError(error, "mark-seen");
    }
  }

  async function markAsUnread(communication: NotificationType) {
    if (!communication?.seenByUserAt(session.user?.id)) return;
    try {
      const updatedNotification = await markNotificationAsUnseen({ notificationId: communication.id.value }).unwrap();
      if (!updatedNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(updatedNotification));
    } catch (error) {
      await handleMutationError(error, "mark-unseen");
    }
  }

  async function archive(communication: NotificationType) {
    if (!communication) return;
    try {
      await archiveNotification({ notificationId: communication.id.value }).unwrap();
      await refreshNotifications();
    } catch (error) {
      await handleMutationError(error, "archive");
    }
  }

  async function unArchive(communication: NotificationType) {
    if (!communication) return;
    try {
      await unarchiveNotification({ notificationId: communication.id.value }).unwrap();
      await refreshNotifications();
    } catch (error) {
      await handleMutationError(error, "unarchive");
    }
  }

  const columns = useMemo(() => {
    const cols = [];

    // The id stays "type": the column's filter and the search still read a row's kind (#6590),
    // and readers' saved column choices name it so.
    cols.push(
      columnHelper.accessor(getNotificationTypeLabel, {
        header: "Subject",
        id: "type",
        meta: { kind: "name" },
        size: 300,
        enableSorting: false,
        enableColumnFilter: true,
        muiFilterTextFieldProps: { placeholder: "Filter by type" },
        Cell: ({ row, table }) => renderSubjectColumn(row.original, table),
      }),
    );
    // The people columns follow the chip: the inbox shows who it is from, Sent who it went to, and the
    // archive, which holds both, shows both (#6719).
    if (peopleColumns.from) {
      cols.push(
        columnHelper.accessor(getNotificationSenderFilterValue, {
          header: "From",
          id: "from",
          meta: { kind: "person" },
          size: 150,
          enableSorting: true,
          enableColumnFilter: true,
          Filter: createCommunicationIndividualFilter(individuals, "From"),
          Cell: ({ row, table }) => renderFromColumn(row.original, table),
        }),
      );
    }
    if (peopleColumns.to) {
      cols.push(
        columnHelper.accessor(getNotificationRecipientFilterValue, {
          header: "To",
          id: "to",
          meta: { kind: "person" },
          size: 150,
          enableSorting: true,
          enableColumnFilter: true,
          Filter: createCommunicationIndividualFilter(individuals, "To"),
          Cell: ({ row, table }) => renderToColumn(row.original, table),
        }),
      );
    }
    cols.push(
      columnHelper.accessor(getNotificationDescriptionFilterValue, {
        header: "Description",
        id: "description",
        meta: { kind: "text" },
        size: 330,
        enableSorting: false,
        enableColumnFilter: true,
        Cell: ({ row }) => renderDescriptionColumn(row.original),
      }),
      columnHelper.accessor(getNotificationCreatedDateSortValue, {
        header: "Created",
        id: "createdDate",
        meta: { kind: "timestamp" },
        // A date is never longer than "10/15/26 03:50 PM"; the fit never cuts one off.
        size: 165,
        minSize: 165,
        enableSorting: true,
        enableColumnFilter: false,
        // The search reads who, what and which category, never when (#6590).
        enableGlobalFilter: false,
        Cell: ({ row }) => renderCreatedDateColumn(row.original),
      }),
    );

    return cols;
  }, [
    individuals,
    peopleColumns.from,
    peopleColumns.to,
    renderSubjectColumn,
    renderFromColumn,
    renderToColumn,
    renderDescriptionColumn,
    renderCreatedDateColumn,
  ]);

  const renderTopToolbarCustomActions = <T extends RowData>(
    props: { table: TableInstance<T> },
  ): JSX.Element => {
    const { table } = props;

    return (
      <ListPageHeading
        title="Communications"
        titleAction={
          isMedDisplaySize ? (
            <Tooltip title="New Message">
              <TableActionIconButton aria-label="New Message" color="primary" onClick={openComposeRoute}>
                <AddCommentIcon fontSize="large" />
              </TableActionIconButton>
            </Tooltip>
          ) : undefined
        }
        tabs={
          <ListPageTabs
            value={activeTab}
            onChange={handleTabChange}
            tabs={[
              {
                value: CommunicationsTab.Notifications,
                label: "Notifications",
                counts: "unread notification(s)",
                count: unreadInbox.counts?.total,
                isLoading: unreadInbox.isLoading,
                badgeColor: "info",
              },
              {
                value: CommunicationsTab.Conversations,
                label: "Conversations",
                counts: "conversation(s) with unread messages",
                count: unreadConversations.data,
                isLoading: unreadConversations.isLoading,
                badgeColor: "info",
              },
            ]}
          />
        }
        chips={
          isNotificationsTab ? (
            <ListChips
              groups={communicationChipGroups}
              selection={chips}
              counts={chipCounts}
              onChange={handleChipsChange}
            />
          ) : (
            <ListChips
              groups={conversationChipGroups}
              selection={conversationChips}
              counts={conversationChipCounts}
              onChange={handleConversationChipsChange}
            />
          )
        }
        mobileToolbar={
          isMedDisplaySize ? (
            // The Views star, the search and the ⋯ menu, which draws Sort and Filter on a card list
            // itself (#7225), on one row, as on every list page (#6720).
            <ListPageMobileToolbar>
              <ViewsMenu views={tableViews} table={table} />
              <ListPageMobileSearch table={table} />
              <TableToolsMenu table={table} />
            </ListPageMobileToolbar>
          ) : undefined
        }
        criteria={<ColumnFilterChips table={table} describeValue={nameOfIndividual} />}
      />
    );
  };

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (errorLoadingQuery) {
      return { color: "error" as const, children: errorLoadingQuery };
    }

    // Each box's bulk actions (#6719). On a phone the rows are cards, ticked by their checkboxes, and the
    // actions are icons.
    const handlers: Readonly<Record<CommunicationBulkAction, () => unknown>> = {
      markRead: markMultipleSeen,
      markUnread: markMultipleUnseen,
      archive: archiveMultiple,
      unarchive: unArchiveMultiple,
    };
    return {
      action: (
        <AlertActions>
          {bulkActions.map((action) => {
            const { label, title, icon } = BULK_ACTION_LABELS[action];
            const onClick = () => void handlers[action]();
            return isMedDisplaySize ? (
              <Tooltip key={action} title={title}>
                <span>
                  <IconButton color="inherit" aria-label={title} onClick={onClick}>
                    {icon}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button key={action} color="inherit" startIcon={icon} onClick={onClick}>
                {label}
              </Button>
            );
          })}
        </AlertActions>
      ),
      sx: (theme: Theme) => ({
        "& .MuiBox-root .MuiStack-root": {
          alignItems: "baseline",
          gap: theme.spacing(1),
        },
      }),
    };
  }, [archiveMultiple, bulkActions, errorLoadingQuery, isMedDisplaySize, markMultipleSeen, markMultipleUnseen, unArchiveMultiple]);

  const tableOptions: MRT_TableOptions<NotificationType | NotificationLoadIssue> = {
    columns,
    data: tableNotifications?.data ?? [],
    rowCount: tableNotifications?.headers.totalElements ?? 0,
    pageCount: Math.ceil((tableNotifications?.headers.totalElements ?? 0) / pagination.pageSize),
    enableRowSelection: true,
    enableRowActions: true,
    enableTableHead: true,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        muiTableHeadCellProps: {
          sx: { px: 0.5 },
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            px: 0.5,
          },
        },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: communicationsSearchPlaceholder(chips),
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: globalFilter ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                size="small"
                onClick={() => setGlobalFilter("")}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    muiTableBodyRowProps: ({ row }) =>
      notificationRowProps(row.original, {
        isArchived: isCommunicationArchived(row.original, chips),
        userId: session.user?.id,
        onSelect: () => {
          void navigateToTarget(row.original);
        },
      }),
    muiTableProps: () => ({
      sx: {
        height: tableNotifications?.data ? "initial" : "100%",
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMedDisplaySize && (
          <ListPageToolbarButtons>
            <ToolbarButtons>
              <ViewsMenu views={tableViews} table={table} />
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <TableToolsMenu table={table} />
            </ToolbarButtons>
            <ToolbarActionButtons>
              <TableActionButton
                color="primary"
                startIcon={<AddCommentIcon />}
                onClick={openComposeRoute}
              >
                New Message
              </TableActionButton>
            </ToolbarActionButtons>
          </ListPageToolbarButtons>
        )}
      </>
    ),
    renderTopToolbarCustomActions,
    renderRowActions: ({ row }) =>
      communicationRowActionsCell(row.original, {
        isInbox,
        isArchived: isCommunicationArchived(row.original, chips),
        userId: session.user?.id,
        onMarkRead: markAsRead,
        onMarkUnread: markAsUnread,
        onArchive: archive,
        onUnarchive: unArchive,
      }),
    renderEmptyRowsFallback: emptyRowsFallback({
      // The Unread and Action required chips are the page's own, and an inbox narrowed to nothing by
      // one of them is not an empty inbox.
      hasPageFilters: isChipOn(chips, UNREAD_PARAMETER) || isChipOn(chips, ACTION_REQUIRED_PARAMETER),
      // A glyph, not the "All caught up" illustration: that sentence was drawn into the artwork, so the
      // page said it and then "No communications" under it (#6099). The heading below is the one message.
      icon: <TaskAltIcon />,
      isError: Boolean(errorLoadingQuery),
      isLoading: queryIsLoading,
      onRetry: () => void refreshNotifications(),
      // The chips name the box; the message has to name what is in it.
      subject,
    }),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnOrder: [
        "type",
        "from",
        "to",
        "description",
        "message",
        "conversationName",
        "createdDate",
        "actions",
      ],
      showGlobalFilter: true,
    },
    manualFiltering: true,
    enableColumnFilters: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minWidth: 0,
        position: "relative"
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden"
      },
    },
    muiTopToolbarProps: {
      sx: {
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        "& > .MuiBox-root": {
          gap: 0,
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        height: "100%",
        "& tr:only-of-type td[colspan]": {
          height: "100%",
          verticalAlign: "middle",
          padding: 0,
          borderBottom: "none",
        },
      },
    },
    muiToolbarAlertBannerProps: muiToolbarAlertBannerProps,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    positionGlobalFilter: "none",
    state: {
      columnFilters,
      globalFilter,
      isLoading: queryIsLoading,
      pagination,
      showAlertBanner: Boolean(errorLoadingQuery),
      showProgressBars: queryIsLoading,
      sorting,
      rowSelection,
      showSkeletons: queryIsLoading && !tableNotifications,
    },
  };

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToCommunications) return <NotAuthorized />;

  if (activeTab === CommunicationsTab.Conversations) {
    return <ConversationTable renderTopToolbarCustomActions={renderTopToolbarCustomActions} onCompose={openComposeRoute} />;
  }
  return (
    <MRTNoMemo<NotificationType | NotificationLoadIssue>
      options={tableOptions}
      persistColumnVisibilityAs="communications"
      cards
    />
  );
}

function getIndividualFilterOptionsFromSearchResults(results: MarketplaceSearchInfoJSON[]): IndividualFilterOption[] {
  return dedupeIndividualFilterOptions(
    results.flatMap((result) => {
      const option = getIndividualFilterOptionFromSearchResult(result);
      return option ? [option] : [];
    }),
  );
}

function getIndividualFilterOptionFromSearchResult(
  result: MarketplaceSearchInfoJSON,
): IndividualFilterOption | undefined {
  if (!result.userId) return undefined;

  const fullName = [result.firstName, result.lastName].filter(Boolean).join(" ").trim();

  return {
    label: fullName || result.email || result.userId,
    secondaryLabel: result.companyName ?? undefined,
    value: result.userId,
  };
}

function getIndividualFilterOptionFromIndividual(individual: Individual): IndividualFilterOption {
  return {
    label: individual.fullName,
    secondaryLabel: individual.entityName,
    value: individual.userId.value,
  };
}

function dedupeIndividualFilterOptions(options: IndividualFilterOption[]): IndividualFilterOption[] {
  return [...new Map(options.map((option) => [option.value, option])).values()];
}

function getSelectedNotificationFilterUserIds(columnFilters: ColumnFiltersState): string[] {
  return columnFilters
    .filter((filter) => (filter.id === "from" || filter.id === "to") && typeof filter.value === "string")
    .map((filter) => filter.value as string)
    .filter((value) => Guid.isValid(value));
}

function getValidNotificationUserIds(
  tableNotifications: PaginatedResponse<NotificationLoadIssue | NotificationType> | undefined,
): Set<string> {
  const validNotifications: NotificationType[] = [];
  if (!tableNotifications) return new Set();

  tableNotifications.data.forEach((notification) => {
    if (!isNotificationLoadIssue(notification)) {
      validNotifications.push(notification);
    }
  });

  const uniqueSenderIds = new Set(
    validNotifications
      .map((notification: NotificationType) => notification.senderUserId?.value)
      .filter((senderId) => senderId !== undefined),
  );
  const uniqueRecipientIds = tableNotifications
    ? new Set(
      validNotifications.flatMap((notification: NotificationType) =>
        notification.recipients.map((recipient) => recipient.userId.value).filter((userId) => userId !== undefined),
      ),
    )
    : new Set<string>();

  return new Set([...uniqueSenderIds, ...uniqueRecipientIds]);
}

/**
 * The label the Type column shows, and the value it filters and sorts on.
 *
 * Exported so it can be covered directly rather than through a rendered page — see the note at the
 * top of `communications-page.spec.tsx`.
 */
export function getNotificationTypeLabel(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "Unknown";

  // A rolled row is its record's notification, "Work Proposal", whatever its newest update was (#6880).
  const topicCategory = getRolledRecordCategory(notification) ?? notification.topic?.category;
  if (!topicCategory) return "Notification";
  // A new-message notification is topic-associated with the forum the message was posted in
  // (#3144), so it arrives under the same `Messaging.Forum` category a forum-level notification
  // would. The label still names the message it announces rather than the forum.
  if (topicCategory === TopicCategory.Messaging.Forum) {
    // A run of messages in one forum is folded into the notification already sitting unread in the
    // recipient's inbox rather than sent one row per message (#830), so one row can stand for
    // several. The label says how many. The Type filter matches on substrings, so a reader filtering
    // for "New Message" still finds the aggregated rows.
    const messageCount = notification.occurrenceCount;
    return messageCount > 1 ? `${messageCount} New Messages` : "New Message";
  }

  return Config.notificationTypes[topicCategory as keyof typeof Config.notificationTypes] ?? "Notification";
}

function getNotificationSenderFilterValue(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "";
  if ("messageInfo" in notification && notification.messageInfo?.senderName?.value) {
    return notification.messageInfo.senderName.value;
  }

  return notification.senderName?.value ?? "System";
}

function getNotificationRecipientFilterValue(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "";
  return notification.recipientName?.value ?? notification.recipients?.map((recipient) => recipient.userId.value).join(", ") ?? "";
}

function getNotificationDescriptionFilterValue(notification: NotificationType | NotificationLoadIssue): string {
  if (isNotificationLoadIssue(notification)) return "";
  return notification.message?.value ?? "";
}

function getNotificationCreatedDateSortValue(notification: NotificationType | NotificationLoadIssue): number {
  if (isNotificationLoadIssue(notification)) return 0;
  return notification.createdDate?.value ?? 0;
}
